import {Divider, Typography} from '@genestack/ui';
import React from 'react';
// tslint:disable-next-line: no-implicit-dependencies
import {TransitionGroup, TransitionStatus} from 'react-transition-group';

import classNames from 'classnames';
import {animationDuration} from './constants';
import queryMPStyles from './query-management-panel.module.css';
import commonMPStyles from '../../../components/management-panel/management-panel.module.css';
import {RecentQuery} from './recent-query';
import {UnresolvedQuery} from './unresolved-query';
import {useMPUnresolvedQueries} from './use-mp-unresolved-queries';
import {isQueryPending, isQueryRunning} from '../utils';
import {CSSTransitionWithRef} from './css-transition-with-ref';

interface Props {
    unresolvedQueriesRequest: ReturnType<typeof useMPUnresolvedQueries>;
    getRecentQueryById: (id: number) => FinishedQuery | undefined;
}

export function UnresolvedQueries(props: Props) {
    const {data} = props.unresolvedQueriesRequest;

    const pendingQueries: PendingQuery[] = data?.filter(isQueryPending) || [];
    const runningQueries: RunningQuery[] = data?.filter(isQueryRunning) || [];

    return (
        <>
            <div className={classNames(queryMPStyles.queryRow, commonMPStyles.tableRow)}>
                <Typography variant="section">Executing queries</Typography>

                <Typography intent="quiet" variant="body">
                    Author
                </Typography>

                <Typography intent="quiet" variant="body">
                    Calculation time / Found results / Status or Queue prediction
                </Typography>
            </div>

            <Divider gap={0} />

            {(function () {
                return (
                    <>
                        <TransitionGroup>
                            {pendingQueries.map((query, index) => (
                                <CSSTransitionWithRef
                                    key={query.id}
                                    timeout={{enter: animationDuration, exit: 0}}
                                    unmountOnExit
                                >
                                    {(transitionStatus: TransitionStatus) => (
                                        <UnresolvedQuery
                                            key={query.id}
                                            query={query}
                                            positionInQueue={pendingQueries.length - index}
                                            isHighlighted={transitionStatus === 'entering'}
                                        />
                                    )}
                                </CSSTransitionWithRef>
                            ))}
                        </TransitionGroup>

                        <TransitionGroup>
                            {runningQueries.map((query) => (
                                <CSSTransitionWithRef
                                    key={query.id}
                                    timeout={{enter: animationDuration, exit: animationDuration}}
                                    exit
                                    in
                                >
                                    {(transitionStatus: TransitionStatus) => {
                                        if (transitionStatus === 'exiting') {
                                            const recentQuery = props.getRecentQueryById(query.id);

                                            return recentQuery ? (
                                                <RecentQuery query={recentQuery} isHighlighted />
                                            ) : null;
                                        }

                                        return <UnresolvedQuery key={query.id} query={query} />;
                                    }}
                                </CSSTransitionWithRef>
                            ))}

                            {!pendingQueries.length && !runningQueries.length && (
                                <CSSTransitionWithRef
                                    key="empty"
                                    timeout={animationDuration}
                                    unmountOnExit
                                >
                                    {(transitionStatus: TransitionStatus) => {
                                        if (transitionStatus === 'exiting') {
                                            return null;
                                        }

                                        return (
                                            <div className={commonMPStyles.emptyTable}>
                                                <Typography intent="quiet">
                                                    No queries executing at the moment
                                                </Typography>
                                            </div>
                                        );
                                    }}
                                </CSSTransitionWithRef>
                            )}
                        </TransitionGroup>
                    </>
                );
            })()}
        </>
    );
}
